import get from 'lodash/get'

const DEFAULT_DECIMAL_PLACES = 2

export default {
  computed: {
    cartDiscountSourceProduct() {
      return this.product || null
    },
    cartDiscountSourceVariant() {
      return this.displayedVariant || this.activeVariant || this.product || null
    },
    cartDiscountThresholds() {
      const rawDiscounts =
        get(this.cartDiscountSourceVariant, 'prices.attributes.d', null) ||
        get(this.cartDiscountSourceVariant, 'prices.attributes.discounts', {})

      return Object.entries(rawDiscounts)
        .map(([percentage, threshold]) => ({
          percentage: Number(percentage),
          threshold: Number(threshold),
        }))
        .filter(item => Number.isFinite(item.percentage) && item.percentage > 0 && Number.isFinite(item.threshold))
        .sort((a, b) => b.percentage - a.percentage)
    },
    cartDiscountUnitPriceWithTax() {
      return Number(get(this.cartDiscountSourceVariant, 'prices.unitWithTax', 0))
    },
    cartDiscountUnitPriceWithoutTax() {
      return Number(get(this.cartDiscountSourceVariant, 'prices.unitWithoutTax', 0))
    },
    cartDiscountBaseValueWithTax() {
      if (!this.$store.state.cart.isLoaded) {
        return 0
      }

      return Number(get(this.$store.state.cart, 'cartData.prices.productDiscountLimitPriceWithVat', 0))
    },
    cartDiscountComparisonValueWithTax() {
      return this.roundCartDiscountPrice(this.cartDiscountBaseValueWithTax + this.cartDiscountUnitPriceWithTax)
    },
    isCartDiscountable() {
      const variantDiscountable = get(this.cartDiscountSourceVariant, 'discountable', null)

      if (variantDiscountable !== null) {
        return Boolean(variantDiscountable)
      }

      const productDiscountable = get(this.cartDiscountSourceProduct, 'discountable', null)
      if (productDiscountable !== null) {
        return Boolean(productDiscountable)
      }

      return this.cartDiscountThresholds.length > 0
    },
    cartDiscountPercentage() {
      if (!this.$store.state.cart.isLoaded || !this.isCartDiscountable || this.cartDiscountUnitPriceWithTax <= 0) {
        return 0
      }

      const activeDiscount = this.cartDiscountThresholds.find(
        discount => this.cartDiscountComparisonValueWithTax >= discount.threshold,
      )

      return activeDiscount ? activeDiscount.percentage : 0
    },
    cartDiscountPriceWithTax() {
      if (this.cartDiscountPercentage <= 0) {
        return 0
      }

      return this.roundCartDiscountPrice(
        this.cartDiscountUnitPriceWithTax * ((100 - this.cartDiscountPercentage) / 100),
      )
    },
    cartDiscountPriceWithoutTax() {
      if (this.cartDiscountPercentage <= 0) {
        return 0
      }

      return this.roundCartDiscountPrice(
        this.cartDiscountUnitPriceWithoutTax * ((100 - this.cartDiscountPercentage) / 100),
      )
    },
    cartDiscountSavingsWithTax() {
      if (!this.showCartDiscountPrice) {
        return 0
      }

      return this.roundCartDiscountPrice(this.cartDiscountUnitPriceWithTax - this.cartDiscountPriceWithTax)
    },
    showCartDiscountPrice() {
      return (
        this.$store.state.cart.isLoaded &&
        this.isCartDiscountable &&
        this.cartDiscountThresholds.length > 0 &&
        this.cartDiscountPercentage > 0 &&
        this.cartDiscountPriceWithTax > 0 &&
        this.cartDiscountPriceWithTax < this.cartDiscountUnitPriceWithTax
      )
    },
    cartDiscountPriceLabel() {
      return this.showCartDiscountPrice ? this.$currencyFormat(this.cartDiscountPriceWithTax) : ''
    },
    cartDiscountPriceWithoutTaxLabel() {
      return this.showCartDiscountPrice ? this.$currencyFormat(this.cartDiscountPriceWithoutTax) : ''
    },
  },
  methods: {
    roundCartDiscountPrice(price) {
      const decimalPlaces = this.$store.state.currency.activeCurrency?.decimalPlaces ?? DEFAULT_DECIMAL_PLACES
      const coeff = Math.pow(10, decimalPlaces)

      return Math.round(Number(price || 0) * coeff) / coeff
    },
  },
}
